<template>
  <v-container fluid class="help-page pa-0">
    <v-row no-gutters>

      <!-- Sticky sidebar -->
      <v-col cols="2" class="d-none d-md-block">
        <div class="sidebar pa-4">
          <div class="sidebar-title mb-4">
            <span>Pap</span><span class="cinzel-a">A</span><span class="cinzel-rest">rtisan</span>
            <v-btn icon x-small @click="$router.push('/')" title="Home" class="ml-auto">
              <v-icon small>mdi-home</v-icon>
            </v-btn>
            <v-btn icon x-small @click="$vuetify.theme.dark = !$vuetify.theme.dark" :title="$vuetify.theme.dark ? 'Light mode' : 'Dark mode'">
              <v-icon small>{{ $vuetify.theme.dark ? 'mdi-white-balance-sunny' : 'mdi-weather-night' }}</v-icon>
            </v-btn>
          </div>
          <v-list dense nav>
            <v-list-item v-for="section in sections" :key="section.id" @click="scrollTo(section.id)" class="sidebar-item">
              <v-list-item-content>
                <v-list-item-title class="sidebar-link" :class="{ active: activeSection === section.id }">
                  {{ section.title }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </div>
      </v-col>

      <!-- Scrollable content -->
      <v-col cols="12" md="10">
        <v-container>
          <v-row justify="center">
            <v-col cols="12" lg="9">

              <v-btn text class="mb-4" @click="closeTab">
                <v-icon left>mdi-close</v-icon> Close Tab
              </v-btn>

              <h1 class="display-1 mb-6">Documentation</h1>

              <!-- Built with Paperize - at top -->
              <v-card class="pa-5 mb-4" id="acknowledgement">
                <h2 class="headline mb-3">Built with Paperize</h2>
                <p>PapArtisan is built on the foundation of <a href="https://beta.editor.paperize.io/" target="_blank">Paperize.io</a>, an open source card game prototyping tool. The inline icon and rich text formatting features are unique to PapArtisan, but the core engine, Google Drive integration, template editor and PDF rendering are Paperize's work.</p>
                <p class="mt-3">If you find this tool useful, please consider supporting the original project:</p>
                <div class="d-flex mt-3" style="gap: 12px;">
                  <v-btn outlined small href="https://www.patreon.com/bePatron?u=67763" target="_blank">
                    <v-icon left small>mdi-heart</v-icon> Support on Patreon
                  </v-btn>
                  <v-btn outlined small href="https://discord.gg/9ggkkQp" target="_blank">
                    <v-icon left small>mdi-message</v-icon> Paperize Discord
                  </v-btn>
                </div>
              </v-card>

              <!-- Getting Started -->
              <v-card class="pa-5 mb-4" id="getting-started">
                <h2 class="headline mb-3">Getting Started</h2>
                <p>PapArtisan is a card game design tool that uses Google Sheets for your data and Google Drive for your assets. Here's the basic workflow:</p>
                <ol class="mt-2">
                  <li>Design your game data in a Google Spreadsheet</li>
                  <li>Organise your images in Google Drive</li>
                  <li>Use PapArtisan to generate print-and-play PDFs</li>
                  <li>Print, play, ponder, repeat!</li>
                </ol>
              </v-card>

              <!-- Logging In -->
              <v-card class="pa-5 mb-4" id="logging-in">
                <h2 class="headline mb-3">Logging In</h2>
                <p>Click "Sign in with Google" on the home screen. PapArtisan will ask for permission to access your Google Drive and Sheets.</p>
                <v-alert type="info" outlined class="mt-3">
                   PapArtisan can read your Google Drive to allow image picking, but can only modify files it created itself. It does not read, copy or transmit any other files.
                </v-alert>
                <p class="mt-3">After logging in, PapArtisan creates a "PapArtisan" folder in your Google Drive containing your database file.</p>
              </v-card>

              <!-- Creating a Game -->
              <v-card class="pa-5 mb-4" id="creating-a-game">
                <h2 class="headline mb-3">Creating a Game</h2>
                <p>Click "New Game" and fill in a title. PapArtisan will automatically create the following in your Google Drive:</p>
                <ul class="mt-2 mb-3">
                  <li>A folder for your game</li>
                  <li>An Images folder inside it</li>
                  <li>A Spreadsheet for your card data</li>
                </ul>
                
              </v-card>

              <!-- Components -->
              <v-card class="pa-5 mb-4" id="components">
                <h2 class="headline mb-3">Components</h2>
                <p>Games are made up of components - a deck of cards, a stack of tiles, player mats, etc. Click "New Component" to create one.</p>
                <p class="mt-2">Each component links to a worksheet in your spreadsheet. The first row contains property names (like "Name", "Cost", "Text"). Each subsequent row is one card or tile.</p>
                <p class="mt-2">Use a "Quantity" column to specify how many copies of each card appear in the deck - PapArtisan handles the duplication automatically.</p>
              </v-card>

              <!-- Template Editor -->
              <v-card class="pa-5 mb-4" id="template-editor">
                <h2 class="headline mb-3">Template Editor</h2>
                <p>The template editor has three sections:</p>
                <ul class="mt-2 mb-3">
                  <li><strong>Layers</strong> - create and manage layers</li>
                  <li><strong>Layer Config</strong> - settings for the selected layer</li>
                  <li><strong>Preview</strong> - see how your cards look</li>
                </ul>
                <p>There are three layer types:</p>
                <ul class="mt-2">
                  <li><strong>Text</strong> - displays text from your spreadsheet</li>
                  <li><strong>Image</strong> - displays images from your Drive</li>
                  <li><strong>Shape</strong> - rectangles, rounded rectangles, ellipses</li>
                </ul>
                <p class="mt-3">Layer order matters - layers further down the list appear on top. Layers can be cloned, hidden, or deleted. There is no undo for deletion.</p>

                <h3 class="subtitle-1 mb-2 mt-4">Edit Item Data</h3>
                <p>The preview panel includes an <strong>Edit Item Data</strong> button below the "Go To Item" dropdown. It lets you edit a spreadsheet row directly within PapArtisan without leaving the app.</p>
                <div class="mt-3 mb-3">
                  <v-btn small disabled>
                    <v-icon left>mdi-grid</v-icon>
                    Edit Item Data
                  </v-btn>
                </div>
                <p>Click it to open a form showing all the fields for the currently previewed item. Edit any values and click <strong>Save</strong> - changes are written back to your Google Sheet immediately and the preview updates automatically.</p>
                <v-alert type="info" outlined class="mt-3">
                  The button only appears when a spreadsheet is linked to the component.
                </v-alert>
              </v-card>
<!-- Data Editor -->
              <v-card class="pa-5 mb-4" id="data-editor">
                <h2 class="headline mb-3">Data Editor</h2>
                <p>The Data Editor tab gives you a side-by-side view of your card previews and spreadsheet data, so you can tweak card content without leaving PapArtisan or switching to Google Sheets.</p>

                <h3 class="subtitle-1 mb-2 mt-4">Card Carousel</h3>
                <p>The top half of the Data Editor shows a scrollable row of rendered card previews. Click a card to select it - the corresponding row in the data table below will highlight. Click and drag the carousel to scroll through many cards quickly.</p>

                <h3 class="subtitle-1 mb-2 mt-4">Data Table</h3>
                <p>The bottom half shows your spreadsheet data as a table. Click any row to select it and jump the carousel to that card. To edit a cell, first select the row by clicking it, then click the specific cell you want to change - an inline edit dialog will appear. Click <strong>Save</strong> to write the change back to your Google Sheet.</p>
                <p class="mt-2">Only the card whose data changed will re-render in the carousel. Other cards are not affected.</p>
                <v-alert type="warning" outlined class="mt-3">
                  Editing cells will replace any spreadsheet formulas with their current plain-text value. If a cell contains a formula you want to keep, edit it in Google Sheets directly.
                </v-alert>

                <h3 class="subtitle-1 mb-2 mt-4">Adding Rows</h3>
                <p>Click <strong>Add Row</strong> at the bottom of the table to append a new blank row to your spreadsheet. All cards re-render after a row is added.</p>

                <h3 class="subtitle-1 mb-2 mt-4">Card Actions</h3>
                <p>Hovering over a card in the carousel reveals three action buttons:</p>
                <ul class="mt-1 mb-2">
                  <li><strong>Copy</strong> - duplicates the row immediately after the current one</li>
                  <li><strong>Export</strong> - downloads a single-card PDF for that item</li>
                  <li><strong>Delete</strong> - permanently removes the row from the spreadsheet (requires confirmation)</li>
                </ul>
                <v-alert type="info" outlined class="mt-2">
                  Copy and Delete trigger a full spreadsheet re-fetch and re-render all cards, since row indices shift.
                </v-alert>
              </v-card>
              <!-- Rich Text Formatting -->
              <v-card class="pa-5 mb-4" id="rich-text">
                <h2 class="headline mb-3">Rich Text Formatting</h2>
                <p>Use these tokens inside any text layer. Type them directly into your spreadsheet cells or text content fields.</p>
                <v-alert color="primary" outlined class="my-3">
                  <span class="token-input">[token:content]</span> — square brackets, token letters, colon, content, closing bracket with no trailing space.
                </v-alert>

                <h3 class="subtitle-1 mb-2 mt-4">Tokens</h3>
                <v-simple-table class="mb-4">
                  <template v-slot:default>
                    <thead><tr><th>You type</th><th>What it does</th><th>Renders as</th></tr></thead>
                    <tbody>
                      <tr><td><span class="token-input">[b:Charge!]</span></td><td>Bold</td><td><strong>Charge!</strong></td></tr>
                      <tr><td><span class="token-input">[i:Ancient Rune]</span></td><td>Italic</td><td><em>Ancient Rune</em></td></tr>
                      <tr><td><span class="token-input">[c:blue:Ice Shard]</span></td><td>Text colour</td><td><span style="color:#3264dc">Ice Shard</span></td></tr>
                      <tr><td><span class="token-input">[f:courier:Tome of Power]</span></td><td>Font change</td><td><span style="font-family:courier">Tome of Power</span></td></tr>
                      <tr><td><span class="token-input">[h:yellow:Legendary]</span></td><td>Highlight behind text</td><td><span style="background:#e6c800;color:#333;padding:1px 4px;">Legendary</span></td></tr>
                      <tr><td><span class="token-input">[icon:sword.png]</span></td><td>Inline image</td><td>⚔ <span class="caption grey--text">(actual image in PDFs)</span></td></tr>
                    </tbody>
                  </template>
                </v-simple-table>

                <h3 class="subtitle-1 mb-2">Named Colours</h3>
                <p>Use these names anywhere a colour is expected — in <span class="token-input">c</span>, <span class="token-input">h</span>, or Magic Properties.</p>
                <div class="d-flex flex-wrap mb-3 mt-2">
                  <v-chip class="ma-1" color="red" dark small>red</v-chip>
                  <v-chip class="ma-1" color="blue" dark small>blue</v-chip>
                  <v-chip class="ma-1" color="green" dark small>green</v-chip>
                  <v-chip class="ma-1" color="orange" dark small>orange</v-chip>
                  <v-chip class="ma-1" color="purple" dark small>purple</v-chip>
                  <v-chip class="ma-1" color="pink" dark small>pink</v-chip>
                  <v-chip class="ma-1" color="#E6C800" small>yellow</v-chip>
                  <v-chip class="ma-1" color="grey" dark small>grey</v-chip>
                  <v-chip class="ma-1" color="grey darken-4" dark small>black</v-chip>
                  <v-chip class="ma-1" color="grey lighten-3" small light>white</v-chip>
                </div>
                <p>Or use any hex code: <span class="token-input">[c:#FF5733:text]</span></p>

                <h3 class="subtitle-1 mb-2 mt-4">Combining Tokens</h3>
                <p>All tokens can be combined by joining their letters. <strong>b</strong> and <strong>i</strong> are flags — they add no argument. <strong>c</strong>, <strong>h</strong>, and <strong>f</strong> each consume one argument slot, in the order the letters appear.</p>
                <v-alert color="primary" outlined class="my-3">
                  <span class="token-input">[chf:purple:pink:courier:Arcane Blast]</span><br>
                  <span class="body-2 mt-2 d-block"><strong>c</strong> → colour = purple &nbsp;|&nbsp; <strong>h</strong> → highlight = pink &nbsp;|&nbsp; <strong>f</strong> → font = courier &nbsp;|&nbsp; remainder = "Arcane Blast"</span>
                </v-alert>
                <p>Swap the letter order and the argument order must follow:</p>
                <v-alert color="secondary" outlined class="my-3">
                  <span class="token-input">[hcf:pink:purple:courier:Arcane Blast]</span> — same result, different letter order<br>
                  <span class="body-2 mt-1 d-block"><strong>h</strong> → highlight = pink &nbsp;|&nbsp; <strong>c</strong> → colour = purple &nbsp;|&nbsp; <strong>f</strong> → font = courier</span>
                </v-alert>
                <v-simple-table class="mb-2 mt-3">
                  <template v-slot:default>
                    <thead><tr><th>You type</th><th>Renders as</th></tr></thead>
                    <tbody>
                      <tr><td><span class="token-input">[bi:Critical Hit!]</span></td><td><strong><em>Critical Hit!</em></strong></td></tr>
                      <tr><td><span class="token-input">[bc:green:Poison]</span></td><td><strong><span style="color:#32b432">Poison</span></strong></td></tr>
                      <tr><td><span class="token-input">[bic:orange:Cursed!]</span></td><td><strong><em><span style="color:#e68200">Cursed!</span></em></strong></td></tr>
                      <tr><td><span class="token-input">[bcf:purple:courier:Shadow Tome]</span></td><td><strong><span style="color:#9632c8;font-family:courier">Shadow Tome</span></strong></td></tr>
                      <tr><td><span class="token-input">[h:yellow:Quest Item]</span></td><td><span style="background:#e6c800;color:#333;padding:1px 4px;">Quest Item</span></td></tr>
                      <tr><td><span class="token-input">[hc:blue:white:Deep Freeze]</span></td><td><span style="background:#3264dc;color:#fff;padding:1px 4px;">Deep Freeze</span></td></tr>
                      <tr><td><span class="token-input">[hf:orange:courier:Wanted Poster]</span></td><td><span style="background:#e68200;color:#333;font-family:courier;padding:1px 4px;">Wanted Poster</span></td></tr>
                      <tr><td><span class="token-input">[hcf:green:black:courier:Venom Rune]</span></td><td><span style="background:#32b432;color:#000;font-family:courier;padding:1px 4px;">Venom Rune</span></td></tr>
                      <tr><td><span class="token-input">[chf:purple:pink:courier:Arcane Blast]</span></td><td><span style="background:pink;color:purple;font-family:courier;padding:1px 4px;">Arcane Blast</span></td></tr>
                      <tr><td><span class="token-input">[bhc:red:white:BOSS FIGHT]</span></td><td><strong style="background:#dc3232;color:#fff;padding:1px 4px;">BOSS FIGHT</strong></td></tr>
                      <tr><td><span class="token-input">[bichf:blue:white:courier:Dragon Slayer]</span></td><td><strong><em><span style="background:#3264dc;color:#fff;font-family:courier;padding:1px 4px;">Dragon Slayer</span></em></strong></td></tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-card>

              <!-- Inline Icons -->
              <v-card class="pa-5 mb-4" id="inline-icons">
                <h2 class="headline mb-3">Inline Icons</h2>
                <p>Icons are images that appear inline with your text - for example "Deal 2 ⚔ damage" where the sword is an actual uploaded image.</p>
                <v-alert type="warning" outlined class="my-3">
                  Icons must be uploaded through PapArtisan. Images added directly to Google Drive won't be accessible. Upload them via the image tool inside your component first.
                </v-alert>
                <p>Once uploaded, reference by exact filename including extension:</p>

                <v-alert color="secondary" outlined class="my-3">
                  <span class="token-input">Deal 2 [icon:sword.png] damage to enemy [icon:heart.png]</span> = "Deal 2<img src="https://game-icons.net/icons/ffffff/000000/1x1/lorc/broadsword.svg" width="30" height="13">damage to enemy<img src="https://game-icons.net/icons/ffffff/000000/1x1/skoll/hearts.svg" width="30" height="13">"

                </v-alert>
                <p><strong>Use square images for best results.</strong> Icons are always scaled to a square, so landscape or portrait images will appear squashed or stretched. Prepare your icons on a square transparent PNG canvas and export at a consistent size like 64×64 or 128×128px. If you're not using square images, experiment with the icon settings or omit the spaces around the token, e.g. <span class="token-input">Deal 2[icon:sword.png]damage</span>. If an icon file cannot be found, the token renders as <span class="token-input">[filename]</span> in place of the image.</p>

                <div class="subtitle-1 mb-2">Icon Settings</div>
                <p>In the template editor, text layers have an <strong>Inline Icons</strong> panel with three controls:</p>
                <ul class="mb-2">
                  <li><strong>Icon Size</strong> - scales the icon relative to the text line height</li>
                  <li><strong>Vertical Offset</strong> - nudges the icon up or down to align with your text baseline</li>
                  <li><strong>Horizontal Gap</strong> - controls the space on both sides of the icon</li>
                </ul>
                <p>Click <strong>Reset to defaults</strong> to restore all three settings if you're unhappy with your adjustments.</p>
              </v-card>

              <!-- Magic Properties -->
              <v-card class="pa-5 mb-4" id="magic-properties">
                <h2 class="headline mb-3">Magic Properties</h2>
                <p>Magic Properties let individual cards override template settings from your spreadsheet - different background colours, font sizes, shapes etc. per card.</p>

                <h3 class="subtitle-1 mb-2 mt-3">How to use them</h3>
                <p>Name a spreadsheet column using this pattern:</p>
                <v-alert color="primary" outlined class="my-3">
                  <span class="token-input">LayerName:attributeName</span><br>
                  <span class="body-2 mt-1 d-block">The layer name is exactly as shown in your template editor.</span>
                </v-alert>
                <p>Example - text layer called <strong>Card Title</strong>, you want different font sizes per card. The attribute is <strong>textSize</strong>, so name the column:</p>
                <v-alert color="secondary" outlined class="my-3"><span class="token-input">Card Title:textSize</span></v-alert>
                <p>Fill each row with a number. Another example - shape layer called <strong>Background</strong> with different fill colours per card:</p>
                <v-alert color="secondary" outlined class="my-3"><span class="token-input">Background:fillColor</span></v-alert>

                <h3 class="subtitle-1 mb-2 mt-3">Available Attributes</h3>
                <v-simple-table>
                  <template v-slot:default>
                    <thead><tr><th>Layer Type</th><th>Attribute</th><th>Accepted Values</th></tr></thead>
                    <tbody>
                      <tr><td>Any</td><td><span class="token-input">visible</span></td><td>true or false</td></tr>
                      <tr><td>Shape</td><td><span class="token-input">shape</span></td><td>rectangle, roundedRectangle, ellipse</td></tr>
                      <tr><td>Shape</td><td><span class="token-input">strokePresent</span></td><td>true or false</td></tr>
                      <tr><td>Shape</td><td><span class="token-input">strokeWidth</span></td><td>number in inches (e.g. 0.05)</td></tr>
                      <tr><td>Shape</td><td><span class="token-input">strokeColor</span></td><td>colour name or hex</td></tr>
                      <tr><td>Shape</td><td><span class="token-input">fillPresent</span></td><td>true or false</td></tr>
                      <tr><td>Shape</td><td><span class="token-input">fillColor</span></td><td>colour name or hex</td></tr>
                      <tr><td>Text</td><td><span class="token-input">textColor</span></td><td>colour name or hex</td></tr>
                      <tr><td>Text</td><td><span class="token-input">textSize</span></td><td>number in points (e.g. 12)</td></tr>
                      <tr><td>Text</td><td><span class="token-input">horizontalAlignment</span></td><td>left, right, or center</td></tr>
                      <tr><td>Text</td><td><span class="token-input">verticalAlignment</span></td><td>top, bottom, or middle</td></tr>
                      <tr><td>Image</td><td><span class="token-input">imageScaling</span></td><td>fitToBox, fillToBox, or stretch</td></tr>
                      <tr><td>Image</td><td><span class="token-input">horizontalAlignment</span></td><td>left, right, or center</td></tr>
                      <tr><td>Image</td><td><span class="token-input">verticalAlignment</span></td><td>top, bottom, or middle</td></tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-card>

              <!-- Fonts -->
              <v-card class="pa-5 mb-4" id="fonts">
                <h2 class="headline mb-3">Fonts</h2>
                <ul>
                  <li><strong>Built-in PDF fonts</strong> - helvetica, courier, times, symbol, zapfdingbats. Small file size, limited character support.</li>
                  <li><strong>Google Fonts</strong> - 900+ fonts, full unicode support, slightly larger file size.</li>
                </ul>
                <p class="mt-3">If characters aren't rendering correctly, try switching to a Google Font.</p>
              </v-card>


		<!-- Corner Insets -->
              <v-card class="pa-5 mb-4" id="corner-insets">
                <h2 class="headline mb-3">Corner Insets</h2>
                <p>Corner insets let you create non-rectangular text areas and shapes by pushing each corner inward. This is useful for fitting text inside diagonal banners, shield shapes, or any angled design.</p>
                <p class="mt-2">Both text layers and rectangle shape layers have a <strong>Corner Insets</strong> panel with four sliders - one per corner. Each slider goes from 0 (no inset) to 1 (fully inset). At 0 the layer is a normal rectangle.</p>
                <v-simple-table class="mt-3 mb-3">
                  <template v-slot:default>
                    <thead><tr><th>Slider</th><th>Effect</th></tr></thead>
                    <tbody>
                      <tr><td><strong>Top Left</strong></td><td>Pushes the top-left corner right</td></tr>
                      <tr><td><strong>Top Right</strong></td><td>Pushes the top-right corner left</td></tr>
                      <tr><td><strong>Bottom Left</strong></td><td>Pushes the bottom-left corner right</td></tr>
                      <tr><td><strong>Bottom Right</strong></td><td>Pushes the bottom-right corner left</td></tr>
                    </tbody>
                  </template>
                </v-simple-table>
                <v-alert type="info" outlined>
                  Text wraps within the inset boundary on each line, at extreme values text may overflow. Highlighting the selected layer will attempt to outline the shape you're creating but may fail to be exact. These are known limitations.
                </v-alert>

<!-- Text Rotation -->
              <v-card class="pa-5 mb-4" id="rotation">
                <h2 class="headline mb-3">Text Rotation</h2>
                <p>Text layers can be rotated to any angle. This is useful for diagonal banners, vertical side labels, and angled text effects.</p>
                <p class="mt-2">In the template editor, text layers have a <strong>Rotation</strong> panel with a slider (0-360°) and quick preset buttons:</p>
                <v-simple-table class="mt-3 mb-3">
                  <template v-slot:default>
                    <thead><tr><th>Preset</th><th>Use case</th></tr></thead>
                    <tbody>
                      <tr><td><strong>0°</strong></td><td>Normal horizontal text</td></tr>
                      <tr><td><strong>45°</strong></td><td>Diagonal banner going up-right</td></tr>
                      <tr><td><strong>-45° (315°)</strong></td><td>Diagonal banner going down-right</td></tr>
                      <tr><td><strong>90°</strong></td><td>Vertical text reading upward</td></tr>
                      <tr><td><strong>-90° (270°)</strong></td><td>Vertical text reading downward</td></tr>
                      <tr><td><strong>180°</strong></td><td>Upside down text</td></tr>
                    </tbody>
                  </template>
                </v-simple-table>
                <v-alert type="info" outlined>
                  Text always rotates around the center of the layer. Position and size your layer first, then apply rotation. The layer dimensions (width and height) are always defined in the unrotated state.
                </v-alert>
                <v-alert type="info" outlined class="mt-3">
                  Corner insets and rich text formatting work with rotation. The red highlight frame in the template editor will also rotate to show the layer boundary.
                </v-alert>
              </v-card>

              <!-- Google Drive -->
              </v-card>
              <v-card class="pa-5 mb-6" id="google-drive">
                <h2 class="headline mb-3">Google Drive Integration</h2>
                <ul>
                  <li>Images can be uploaded through PapArtisan or picked directly from your Google Drive using the image picker</li>
                  <li>Spreadsheets must be created through PapArtisan - external spreadsheets cannot be linked</li>
                  <li>Your database is stored as <strong>papartisan_database.json</strong> in your PapArtisan Drive folder</li>
                  <li>The PapArtisan folder in Google Drive can be moved or renamed without breaking anything</li>
                </ul>
              </v-card>

              <!-- About PapArtisan -->
              <v-card class="pa-5 mb-6" id="about">
                <h2 class="headline mb-3">About PapArtisan</h2>
                <p>PapArtisan  is a personal project - there are no servers, no accounts, and no tracking. Everything you create is stored in your own Google Drive. PapArtisan has no backend of its own and never sees your data.</p>
                <p>It is built on the foundations of <a href="https://beta.editor.paperize.io" target="_blank">Paperize.io</a>, an open source card game prototyping tool. The inline icon and rich text features are unique to PapArtisan, but the core engine, Google Drive integration, template editor, and PDF rendering are Paperize's work.</p>
              </v-card>

            </v-col>
          </v-row>
        </v-container>
      </v-col>

    </v-row>
  </v-container>
</template>

<script>
export default {
  name: 'HelpPage',
  data() {
    return {
      activeSection: '',
      sections: [
        { id: 'acknowledgement', title: 'Built with Paperize' },
        { id: 'getting-started', title: 'Getting Started' },
        { id: 'logging-in', title: 'Logging In' },
        { id: 'creating-a-game', title: 'Creating a Game' },
        { id: 'components', title: 'Components' },
        { id: 'template-editor', title: 'Template Editor' },
        { id: 'data-editor', title: 'Data Editor' },
        { id: 'rich-text', title: 'Rich Text Formatting' },
        { id: 'inline-icons', title: 'Inline Icons' },
        { id: 'magic-properties', title: 'Magic Properties' },
        { id: 'fonts', title: 'Fonts' },
	{ id: 'corner-insets', title: 'Corner Insets' },
        { id: 'rotation', title: 'Text Rotation' },
        { id: 'google-drive', title: 'Google Drive' },
        { id: 'about', title: 'About PapArtisan' },
      ]
    }
  },
  methods: {
    scrollTo(id) {
      const el = document.getElementById(id)
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        this.activeSection = id
      }
    },
    closeTab() {
      window.close()
    }
  }
}
</script>

<style scoped>
.sidebar {
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  overflow-y: auto;
  border-right: 1px solid rgba(128,128,128,0.2);
  width: 16.666%;
}
.sidebar-title {
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 1px;
  padding: 0 8px;
}
.cinzel-a {
  font-family: 'Cinzel', serif;
  font-style: italic;
  font-size: 1.1em;
  font-weight: 800;
}
.cinzel-rest {
  font-family: 'Cinzel', serif;
  font-style: italic;
}
.sidebar-link {
  font-size: 13px !important;
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.2s;
}
.sidebar-link:hover {
  opacity: 1;
}
.sidebar-link.active {
  opacity: 1;
  font-weight: 600;
  color: var(--v-primary-base);
}
.token-input {
  font-family: 'Courier New', courier, monospace !important;
  font-size: 13px;
  background: rgba(128,128,128,0.15);
  padding: 2px 6px;
  border-radius: 4px;
  border: 1px solid rgba(128,128,128,0.3);
}
</style>